import _ from '@/utils/lodash-optimized'
import moment from 'moment'
import React from 'react'
import access from 'shared_src/access'
import EncounterDetailsDict from 'shared_src/dictionaries/encounters/EncounterDetails'

import {
  CheckboxInput,
  DateInput,
  NumbersTextInput,
  RadioGroupInput,
  SelectInput,
  TextAreaInput,
  TextInput,
  today,
  Validators
} from '../shared/FormBuilder'
import {
  RequiredSpan
} from '../shared/NarusMaterial'

const formattedSeconds = sec =>
  `${Math.floor(sec / 60)}:${`0${sec % 60}`.slice(-2)}`

const renderTime = () => {
  const timeOptions = [
    {
      value: 'computed',
      label: 'Computed'
    },
    {
      value: 'override',
      label: 'Override'
    },
    {
      value: 'non-billable',
      label: 'Non-Billable'
    }
  ]
  return (
    <RadioGroupInput
      options={timeOptions}
      name='time'
      noClear
      validators={[ Validators.required() ]}
    />
  )
}

function overrideDateValidator (value) {
  return function getResult () {
    if (
      (value.dateOfServiceOverridden && !value.dateOfServiceOverrideDate) ||
      (value.dateOfServiceOverridden &&
        moment(value.dateOfServiceOverrideDate).isBefore(
          moment().subtract(90, 'days')
        ))
    ) {
      return 'You must specify a date of service that is less than 90 days in the past.'
    }
    return null
  }
}

function overrideValidator (value) {
  return function getResult () {
    if (
      (!value.timeHours && !value.timeMinutes) ||
      (value.timeHours <= 0 && value.timeMinutes <= 0)
    ) {
      return 'You need to set hours or minutes'
    }
    return null
  }
}

const renderTimeDetails = value => {
  if (value.time === 'override') {
    return (
      <div>
        <div
          className='row'
          key='time'
        >
          <div className='col-xs-3'>
            <NumbersTextInput
              negative={false}
              name='timeHours'
              label='Hours'
              validators={[
                Validators.required(),
                Validators.integer(),
                overrideValidator(value)
              ]}
            />
          </div>
          <div className='col-xs-3'>
            <NumbersTextInput
              negative={false}
              name='timeMinutes'
              label='Minutes'
              validators={[
                Validators.required(),
                Validators.integer(),
                overrideValidator(value)
              ]}
            />
          </div>
          <div className='col-xs-6'>
            <TextInput
              name='timeDetails'
              label='Override Comments'
              validators={[ Validators.required() ]}
            />
          </div>
        </div>
        <div
          className='row'
          key='date'
        >
          <div className='col-xs-3'>
            <CheckboxInput
              label='Date of Service Override'
              name='dateOfServiceOverridden'
            />
          </div>
          <div className='col-xs-3'>
            <DateInput
              label='Date of Service'
              name='dateOfServiceOverrideDate'
              maxDate={today}
              minDate={moment().subtract(89, 'days')}
              disabled={!value.dateOfServiceOverridden}
              validators={[ overrideDateValidator(value) ]}
            />
          </div>
        </div>
      </div>
    )
  }
  return null
}

const renderEncounterTime = (value, duration) => {
  return (
    <div>
      <h2>
        Encounter Time {formattedSeconds(duration)}
      </h2>
      <div style={{ marginTop: '10px' }}>
        {renderTime()}
      </div>
      {renderTimeDetails(value)}
    </div>

  )
}

const isTypeRequired = encounter =>
    encounter?.document?.type !== 'administrative' ||
    encounter?.document?.type !== 'grandRounds'

const renderReportType = encounter => {
  const reportTypes = [
    {
      label: 'APS',
      value: 'APS'
    },
    {
      label: 'Research',
      value: 'Research'
    }
  ]
  return (
    <>
        {encounter?.document?.type === 'cms' &&
        <div className='section'>
          <h2>Report Type</h2>
          <div className='row'>
            <div className='col-xs-12 col-sm-6'>
              <SelectInput
                options={reportTypes}
                label='Report Type'
                name='reportType'
                placeholder='Care Management Summary'
              />
            </div>
          </div>
        </div>
      }
    </>
  )
}

const renderUrgency = encounter => {
  const urgentRadioButtons = [
    {
      label: 'Urgent',
      value: 'urgent'
    },
    {
      label: 'Non-urgent',
      value: 'non-urgent'
    }
  ]
  return (
    <div className='section'>
      <h2>
        Urgency
        {' '}
        {isTypeRequired(encounter) && <RequiredSpan text='Value' />}
      </h2>
      <RadioGroupInput
        name='urgency'
        options={urgentRadioButtons}
        noClear
        validators={[ isTypeRequired(encounter) && Validators.required() ]}
      />
    </div>
  )
}

const renderDoNotBill = encounter => {
  return (
    <div className='section'>
      <CheckboxInput
        label='Broker/CSR Encounter - Do Not Bill'
        value={encounter.document.agentDoNotBill}
        name='agentDoNotBill'
      />
    </div>
  )
}

const renderNarrative = encounter => (
  <div className='row'>
    <div className='col-xs-12'>
      <TextAreaInput
        name='narrative'
        label='Narrative'
        validators={[ isTypeRequired(encounter) && Validators.required() ]}
      />
    </div>
  </div>
)

const isAllowed = (clientName, profile, allowRules) => {
  const role = (profile?.app_metadata?.roles ?? {})[`${clientName}-compassion`]

  return allowRules.roles.includes(role)
}

const isEncounterTypeRequired = type => {
  if (!type || type === 'patient') {
    return 'Please select encounter type to continue'
  }

  return null
}

const renderEncounterType = (clientName, profile, type) => {

  console.log({ clientName, profile, access })

  const types = [
    // isAllowed(clientName, profile, access.home) && { //Hidden for ticket CM-8727
    //   label: 'Home Encounter',
    //   value: 'home'
    // },
    isAllowed(clientName, profile, access.phone) && {
      label: 'Mobile Message Encounter',
      value: 'mobile'
    },
    isAllowed(clientName, profile, access.phone) && {
      label: 'Email Encounter',
      value: 'email'
    },
    isAllowed(clientName, profile, access.phone) && {
      label: 'Telephonic Encounter',
      value: 'phone'
    },
    isAllowed(clientName, profile, access.administrative) && {
      label: 'Administrative Encounter',
      value: 'administrative'
    }
  ].filter(Boolean)

  const errors =
    (!type && [ 'Please select encounter type to continue' ]) || null
  return (
    <div className='section'>
      <h2>
        Encounter Type
        {' '}
        <RequiredSpan text='Value' />
      </h2>
      <div>
        <RadioGroupInput
          name='type'
          options={types}
          noClear
          validators={[ isEncounterTypeRequired ]}
          errors={errors}
        />
        {/* )} */}
      </div>
    </div>
  )
}

const renderPhoneEncounterStuff = () => (
  <div className='section'>
    <h2>
      Call Origin
      {' '}
      <RequiredSpan text='Value' />
    </h2>

    <RadioGroupInput
      options={EncounterDetailsDict.origins}
      name='origin'
      noClear
      validators={[ Validators.required() ]}
    />

  </div>
)

const appendLucentRx = (reasons, clientAttributes) => {
  if (clientAttributes?.includes('LucentRx')) {
    return [{
      code: 'lucentrx',
      label: 'LucentRx',
      level: 1,
      value: 'lucentrx'
    }].concat(reasons)
  }
  return reasons
}

export const getReasonOptions = (organization, type, origin, clientAttributes) => {
  if (type === 'phone' && origin === 'inbound') {
    return appendLucentRx(EncounterDetailsDict.reasonsPhoneInbound,
      clientAttributes)
  }
  if (type === 'phone' && origin === 'outbound') {
    return appendLucentRx(EncounterDetailsDict.reasonsPhoneOutbound,
      clientAttributes)
  }
  if (type === 'mobile' && origin === 'inbound') {
    return appendLucentRx(EncounterDetailsDict.reasonsPhoneInbound,
      clientAttributes)
  }
  if (type === 'mobile' && origin === 'outbound') {
    return appendLucentRx(EncounterDetailsDict.reasonsPhoneOutbound,
      clientAttributes)
  }
  if (type === 'email' && origin === 'inbound') {
    return appendLucentRx(EncounterDetailsDict.reasonsPhoneInbound,
      clientAttributes)
  }
  if (type === 'email' && origin === 'outbound') {
    return appendLucentRx(EncounterDetailsDict.reasonsPhoneOutbound,
      clientAttributes)
  }
  if (type === 'home') {
    return appendLucentRx(EncounterDetailsDict.reasonsHome, clientAttributes)
  }
  return []
}

const appendVendor = (participants, clientAttributes, reason) => {
  if (clientAttributes?.includes('LucentRx') && reason === 'lucentrx') {
    return [{
      code: 'vendor',
      label: 'Vendor',
      value: 'vendor'
    }].concat(participants)
  }
  return participants
}

const appendCuraLinc = (participants, reason) => {
  if (reason === 'bh-coaching') {
    if (!participants.find(item => item.code === 'curalinc')) {
      return [{
        code: 'curalinc',
        label: 'CuraLinc',
        value: 'curalinc'
      }].concat(participants)
    }
    return participants
  }
  return participants.filter(p => p.code !== 'curalinc')
}

const getParticipantOptions = (type, status, dead, clientAttributes, reason) => {
  if (type !== 'phone' && type !== 'home' && type !== 'mobile' && type !== 'email') {
    throw new Error(
      'There is no Participant field for non-phone and non-home encounters'
    )
  }
  const participants = appendVendor(EncounterDetailsDict.participants[type][
    (status === 'finished' && dead && 'expired') || status
  ], clientAttributes, reason)

  return appendCuraLinc(participants, reason)
}

const getOutcomeOptions = (type, status, dead, participant, reason) => {
  if (![ 'phone', 'home', 'mobile', 'email' ].includes(type)) {
    throw new Error(
      'There is no Outcome field for non-phone and non-home encounters'
    )
  }

  if (reason === 'bh-coaching') {
    return EncounterDetailsDict.outcomes.bh
  }

  if (status === 'finished' && dead) {
    status = 'expired'
  }

  return EncounterDetailsDict.outcomes[type]?.[status]?.[participant] ?? []
}

const renderDispositionAndReasonStuff = ({
  episodeOfCare,
  organization,
  encType,
  origin,
  participant,
  clientAttributes,
  reason
}) => {
  const { status, dead } = episodeOfCare
  return (
    <div>
      <h2>
        Disposition Information
        {' '}
        <RequiredSpan text='Value' />
      </h2>
      <div style={{ display: 'flex', gap: '30px' }}>
        <SelectInput
          name='reason'
          label='Encounter Reason'
          options={getReasonOptions(organization, encType, origin, clientAttributes)}
          validators={[ Validators.required() ]}
          noSort
        />
        <SelectInput
          name='participant'
          label='Participant'
          options={getParticipantOptions(encType, status, dead, clientAttributes, reason)}
          validators={[ Validators.required() ]}
          noSort
        />
        <SelectInput
          name='outcome'
          label='Outcome'
          options={getOutcomeOptions(encType, status, dead, participant, reason)}
          validators={[ Validators.required() ]}
          noSort
        />
      </div>
    </div>
  )
}

export {
  formattedSeconds,
  renderEncounterTime,
  renderUrgency,
  renderReportType,
  renderNarrative,
  renderEncounterType,
  renderPhoneEncounterStuff,
  renderDispositionAndReasonStuff,
  getParticipantOptions,
  getOutcomeOptions,
  renderDoNotBill
}
